import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { formatDateRange } from '../utils/dateRange.js';

const ReportingPeriodContext = createContext(null);

/**
 * Calculate date range for a preset
 */
function calculatePresetRange(presetId) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (presetId) {
    case 'current-month':
      // Current month: full calendar month (end-exclusive)
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1); // First day of next month (end-exclusive)
      return { start: currentMonthStart, end: currentMonthEnd };
    
    case 'previous-month':
      // Previous month: full previous calendar month (end-exclusive)
      const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month (end-exclusive)
      return { start: prevMonthStart, end: prevMonthEnd };
    
    case 'past-3-months':
      // Last 3 Months: 3 full months ending at start of current month (end-exclusive)
      const past3Start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const past3End = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month (end-exclusive)
      return { start: past3Start, end: past3End };
    
    case 'past-6-months':
      // Last 6 Months: 6 full months ending at start of current month (end-exclusive)
      const past6Start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const past6End = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month (end-exclusive)
      return { start: past6Start, end: past6End };
    
    case 'past-12-months':
      // Last 12 Months: 12 full months ending at start of current month (end-exclusive)
      const past12Start = new Date(now.getFullYear(), now.getMonth() - 12, 1);
      const past12End = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month (end-exclusive)
      return { start: past12Start, end: past12End };
    
    case 'next-3-months':
      // Next 3 Months: includes current month, ends 3 months from now (end-exclusive)
      const next3Start = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
      const next3End = new Date(now.getFullYear(), now.getMonth() + 3, 1); // First day of month + 3 months (end-exclusive)
      return { start: next3Start, end: next3End };
    
    case 'next-6-months':
      // Next 6 Months: includes current month, ends 6 months from now (end-exclusive)
      const next6Start = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
      const next6End = new Date(now.getFullYear(), now.getMonth() + 6, 1); // First day of month + 6 months (end-exclusive)
      return { start: next6Start, end: next6End };

    case 'this-year':
      // This Year: full calendar year Jan 1 - Dec 31 (end-exclusive)
      const thisYearStart = new Date(now.getFullYear(), 0, 1);
      const thisYearEnd = new Date(now.getFullYear() + 1, 0, 1); // Jan 1 next year (end-exclusive)
      return { start: thisYearStart, end: thisYearEnd };

    case 'next-year':
      // Next Year: full calendar year Jan 1 - Dec 31 (end-exclusive)
      const nextYearStart = new Date(now.getFullYear() + 1, 0, 1);
      const nextYearEnd = new Date(now.getFullYear() + 2, 0, 1); // Jan 1 the year after (end-exclusive)
      return { start: nextYearStart, end: nextYearEnd };

    case 'last-year':
      // Last Year: full calendar year Jan 1 - Dec 31 (end-exclusive)
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear(), 0, 1); // Jan 1 this year (end-exclusive)
      return { start: lastYearStart, end: lastYearEnd };

    default:
      // Default to Last 3 Months (end-exclusive)
      const defaultStart = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const defaultEnd = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month (end-exclusive)
      return { start: defaultStart, end: defaultEnd };
  }
}

/**
 * Get preset label
 */
function getPresetLabel(presetId) {
  const labels = {
    'current-month': 'Current Month',
    'previous-month': 'Previous Month',
    'past-3-months': 'Last 3 Months',
    'past-6-months': 'Last 6 Months',
    'past-12-months': 'Last 12 Months',
    'next-3-months': 'Next 3 Months',
    'next-6-months': 'Next 6 Months',
    'this-year': 'This Year (Jan - Dec)',
    'next-year': 'Next Year',
    'last-year': 'Last Year',
    'custom': 'Custom Range',
  };
  return labels[presetId] || 'Last 3 Months';
}

export function ReportingPeriodProvider({ children }) {
  const [presetId, setPresetId] = useState('current-month'); // Default: Current Month
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  // Calculate current range based on preset or custom
  const range = useMemo(() => {
    if (presetId === 'custom' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return calculatePresetRange(presetId);
  }, [presetId, customStart, customEnd]);

  // Set preset (resets custom range)
  const setPreset = useCallback((newPresetId) => {
    setPresetId(newPresetId);
    if (newPresetId !== 'custom') {
      setCustomStart(null);
      setCustomEnd(null);
    }
  }, []);

  // Set custom range
  const setCustomRange = useCallback((start, end) => {
    setPresetId('custom');
    setCustomStart(start);
    setCustomEnd(end);
  }, []);

  // Get resolved label
  const getResolvedLabel = useCallback(() => {
    return getPresetLabel(presetId);
  }, [presetId]);

  // Get resolved span text
  const getResolvedSpanText = useCallback(() => {
    return formatDateRange(range.start, range.end);
  }, [range]);

  // Backward compatibility: selectedMonth (for pages that still reference it)
  const selectedMonth = useMemo(() => {
    return range.start;
  }, [range.start]);

  const value = useMemo(() => ({
    // New range-based API
    rangeStart: range.start,
    rangeEnd: range.end,
    presetId,
    setPreset,
    setCustomRange,
    getResolvedLabel,
    getResolvedSpanText,
    // Backward compatibility
    selectedMonth,
    setSelectedMonth: (month) => {
      // If setting a month, convert to "current-month" preset if it matches, otherwise use custom
      const monthDate = month instanceof Date ? month : new Date(month);
      const now = new Date();
      if (monthDate.getFullYear() === now.getFullYear() && monthDate.getMonth() === now.getMonth()) {
        setPreset('current-month');
      } else {
        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
        setCustomRange(monthStart, monthEnd);
      }
    },
  }), [range, presetId, setPreset, setCustomRange, getResolvedLabel, getResolvedSpanText, selectedMonth]);

  return (
    <ReportingPeriodContext.Provider value={value}>
      {children}
    </ReportingPeriodContext.Provider>
  );
}

export function useReportingPeriod() {
  const context = useContext(ReportingPeriodContext);
  if (!context) {
    throw new Error('useReportingPeriod must be used within ReportingPeriodProvider');
  }
  return context;
}

